/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { PageViewSwitcher } from "components/navigationBar/PageViewSwitcher";
import { inject } from "mobx-react";
import { pageViewModifiers, docViewModes } from "constants/enums";
import {
  showFitWidthMode,
  showFitPageMode,
  showFixedWidthMode,
  showTwoColumnButton,
  showFullScreenButton,
  showViewWithScrollButton,
  showZoomUI,
  showZoomControl
} from "selectors/hudSelectors";
import { getPageViewModifier, getIsCommentsPanelVisible } from "selectors/zoom";
import { getViewPortWidthWithoutPagesViewScrollbar } from "selectors/documentViewSelectors";
import { getShouldUseModernViewer } from "selectors/appSelectors";
import { HIDE_HUD_ICONS_THRESHOLD } from "constants/constants";
import { isPersonalCommentingEnabled } from "utils/FeatureFlagUtils";
import {
  shouldStartRendering,
  getTotalPages,
  isTwoColumnViewMode,
  isCoverPageMode,
  isViewWithScrolling
} from "selectors/documentSelectors";
import {
  fitPageHandler,
  fitWidthHandler,
  fixWidthHandler,
  twoColumnHandler,
  coverModeHandler,
  fullScreenModeHandler,
  fitOneFullPageHandler,
  viewWithScrollingToggleHandler
} from "services/NavigationBarService";
import { shouldEnableFitOneFullPageMode } from "selectors/pageByPageSelectors";
import { withKeyboardShortcutLabel } from "components/hocs/withKeyboardShortcutLabel";
import { KEYBOARD_SHORTCUTS } from "constants/constants";

const mapStoreToProps = (stores, ownProps) => ({
  fitPageHandler,
  fitWidthHandler,
  fixWidthHandler,
  twoColumnHandler,
  coverModeHandler,
  fullScreenModeHandler,
  fitOneFullPageHandler,
  viewWithScrollingToggleHandler,
  showFitWidthMode: showFitWidthMode(stores),
  isViewWithScrolling: isViewWithScrolling(stores),
  showFitPageMode: showFitPageMode(stores),
  showZoomUI: showZoomUI(stores),
  showZoomControl: showZoomControl(stores),
  showTwoColumnMode: showTwoColumnButton(stores),
  showFullScreenMode: showFullScreenButton(stores),
  showViewWithScrollButton: showViewWithScrollButton(stores),
  isTwoColumnViewMode: isTwoColumnViewMode(stores),
  isCoverPageMode: isCoverPageMode(stores),
  shouldEnableFitOneFullPageMode: shouldEnableFitOneFullPageMode(stores),
  showFixedWidthMode: (() =>
    getViewPortWidthWithoutPagesViewScrollbar(stores) > HIDE_HUD_ICONS_THRESHOLD && showFixedWidthMode(stores))(),
  intl: ownProps.intl,
  shouldDisable: !shouldStartRendering(stores) || getTotalPages(stores) === 0,
  activeViewMode: (() => {
    const pageViewModifier = getPageViewModifier(stores);
    let isFitWidthModeOn = pageViewModifier === pageViewModifiers.FIT_WIDTH;
    if (!getShouldUseModernViewer(stores)) {
      isFitWidthModeOn =
        isFitWidthModeOn ||
        (isPersonalCommentingEnabled(stores) &&
          getIsCommentsPanelVisible(stores) &&
          pageViewModifier === pageViewModifiers.FIXED_WIDTH);
    }
    if (isFitWidthModeOn) return pageViewModifiers.FIT_WIDTH;
    else if (pageViewModifier === pageViewModifiers.FIT_PAGE) return pageViewModifiers.FIT_PAGE;
    else if (pageViewModifier === pageViewModifiers.FIXED_WIDTH) return pageViewModifiers.FIXED_WIDTH;
    return pageViewModifiers.NO_MOD;
  })(),
  handlePanelToggle: ownProps.handlePanelToggle,
  showFlyover: ownProps.showFlyover,
  activeDocViewMode: (() => (isTwoColumnViewMode(stores) ? docViewModes.TWO_COLUMN : docViewModes.CLASSIC_VIEW))()
});

const PageViewSwitcherContainer = inject(mapStoreToProps)(
  withKeyboardShortcutLabel([KEYBOARD_SHORTCUTS.FULL_SCREEN], PageViewSwitcher)
);
export { PageViewSwitcherContainer };
